import React from 'react';
import './styles/index.scss';

export type AnnotationMarkProps = {
  /**
   * Every annotation covering this fragment. More than one is the NORMAL case,
   * not an edge case: the sections layer emits a `section` and a
   * `sectionHeader` per section, and the header span sits entirely inside its
   * parent section's span (measured: 22 of 22 on a sample body, and the corpus
   * is exactly 1495 of each kind). The predecessor rendered that as two nested
   * <mark> elements, which doubled the background and made the ids arbitrarily
   * inner/outer; one element carrying both ids is both correct and legible.
   */
  annotationIds?: string[];
  /** Distinct kinds covering this fragment, so CSS can style headers apart. */
  annotationKinds?: string[];
  /** Source offsets of this fragment in the normalized Markdown body. */
  srcStart?: number;
  srcEnd?: number;
  /**
   * Invoked with every id covering this fragment when the coder activates it.
   * Absent for a display-only body, in which case the fragment renders as an
   * inert `<mark>`.
   */
  onActivate?: (annotationIds: string[]) => void;
  /** Accessible label for an activatable fragment. */
  activateLabel?: string;
  children?: React.ReactNode;
};

/**
 * Renders one annotated fragment of the body.
 *
 * The `data-src-*` attributes are the rendered-DOM -> source-offset mapping.
 * They are free here (the offsets are already in hand from the mdast node) and
 * they are what a string-splicing approach structurally cannot provide: with
 * them, an element in the document can be traced back to the exact byte range
 * of the normalized body it came from, which is the precondition for
 * click-to-annotate rather than display-only highlighting.
 *
 * An activatable fragment renders as a real `<button>`, not a `<mark>` wearing
 * `role="button"`: the native element brings focus, Enter/Space and screen
 * reader semantics with it rather than re-implementing them. It is styled back
 * down to inline text, since it sits mid-sentence. Note that one annotation can
 * span several fragments and therefore several buttons -- each activates the
 * same annotation, which is what the id list is for.
 */
export const AnnotationMark: React.FC<AnnotationMarkProps> = ({
  annotationIds,
  annotationKinds,
  srcStart,
  srcEnd,
  onActivate,
  activateLabel,
  children
}) => {
  const ids = annotationIds || [];

  const shared = {
    className: 'cmx-document-content-annotation-mark',
    'data-annotation-id': ids.join(' ') || undefined,
    'data-annotation-kind': (annotationKinds || []).join(' ') || undefined,
    'data-src-start': srcStart,
    'data-src-end': srcEnd
  };

  if (typeof onActivate !== 'function') {
    return <mark {...shared}>{children}</mark>;
  }

  return (
    <button
      {...shared}
      type="button"
      className={`${shared.className} cmx-document-content-annotation-mark--activatable`}
      aria-label={activateLabel}
      onClick={() => onActivate(ids)}
    >
      {children}
    </button>
  );
};
